import { type FC, useEffect, useState } from 'react'

import { CamOff } from '@liveHelper/assets/CamOff'
import { CamOn } from '@liveHelper/assets/CamOn'
import { ExclamationMark } from '@liveHelper/assets/YellowExclamationMark'
import {
  deviceButtonContainer,
  permissionBubble,
} from '@liveHelper/components/shared/shared.twind'
import { useCameraPermission } from '@liveHelper/hooks/useCameraPermission'
import {
  actionsSelector,
  cameraEnabledSelector,
  paramsSelector,
  useStore,
} from '@liveHelper/models/store'
import { CAMERA_BUTTON_OFF, CAMERA_BUTTON_ON } from '@liveHelper/testIds'
import { ActionButton } from '@oneToOne/shared/styles'

import { useCheckCameraPermission } from '@core/devicePermissions/useCheckCameraPermission'
import { useContextTranslation } from '@core/i18n'
import { logMessage } from '@core/logging'
import { getGuestId } from '@core/tracking/guestId'
import { useTwindContext } from '@core/twind'

interface CamButtonProps {
  isMinimized?: boolean
}

/**
 * CamButton Component
 *
 * A button component for toggling camera on/off state.
 */
export const CamButton: FC<CamButtonProps> = ({ isMinimized }) => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const params = useStore(paramsSelector)
  const cameraEnabled = useStore(cameraEnabledSelector)
  const { cameraEnabledStateToggled } = useStore(actionsSelector)

  const guestId = getGuestId()
  const permission = useCameraPermission()

  const [permissionNeeded, setPermissionNeeded] = useState<boolean>(false)
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(false)

  const { initiateCameraAccess } = useCheckCameraPermission({
    onAllowed: () => {
      cameraEnabledStateToggled()
      setPermissionNeeded(false)
      setShowWarningMessage(false)
    },
    onRejected: () => {
      setPermissionNeeded(true)
      setShowWarningMessage(false)
      logMessage('camera permission rejected', {
        data: {
          visitor_id: guestId,
          chat_channel_id: params.chat_channel_id,
        },
      })
    },
    onDismissed: () => {
      setPermissionNeeded(true)
      setShowWarningMessage(false)
      logMessage('camera permission dismissed', {
        data: {
          visitor_id: guestId,
          chat_channel_id: params.chat_channel_id,
        },
      })
    },
  })

  useEffect(() => {
    if (permission === 'granted') {
      setPermissionNeeded(false)
    } else {
      if (permission === 'prompt' || permission === 'denied') {
        setPermissionNeeded(true)
        if (cameraEnabled) {
          cameraEnabledStateToggled()
        }
      }
    }
  }, [cameraEnabled, cameraEnabledStateToggled, permission])

  const onClick = () => {
    if (!cameraEnabled) {
      if (permissionNeeded) {
        setShowWarningMessage(true)
      }
      initiateCameraAccess()
    } else {
      cameraEnabledStateToggled()
    }
  }

  return (
    <div className={tw?.(deviceButtonContainer)}>
      {showWarningMessage ? (
        <div
          className={tw?.(
            permissionBubble,
            'absolute',
            'text-base',
            'italic',
            'rounded',
            'py-1',
            'px-2',
            'mb-5',
          )}
        >
          {t('oneToOne:Permission required')}
        </div>
      ) : null}
      <ActionButton
        data-testid={cameraEnabled ? CAMERA_BUTTON_ON : CAMERA_BUTTON_OFF}
        aria-label={
          cameraEnabled
            ? t(`oneToOne:Turn your camera off`)
            : t(`oneToOne:Turn your camera on`)
        }
        actionType={cameraEnabled ? 'neutral' : 'danger'}
        shape="circle"
        buttonSize={isMinimized ? 'md' : 'lg'}
        onClick={onClick}
        icon={
          cameraEnabled ? (
            <CamOn
              width={isMinimized ? '18px' : '26px'}
              fill="var(--white)"
              label=""
              aria-hidden
            />
          ) : (
            <CamOff
              width={isMinimized ? '18px' : '26px'}
              fill="var(--white)"
              label=""
              aria-hidden
            />
          )
        }
      >
        {permissionNeeded ? (
          <ExclamationMark
            className={tw?.('absolute', 'top-0', 'right-0')}
            label={t('oneToOne:Permission required')}
          />
        ) : null}
      </ActionButton>
    </div>
  )
}
